import { Div, FormStatus, InfoCard, P, ProgressIndicator } from '@dnb/eufemia';
import { type JSX, useMemo } from 'react';

import { useDebounce } from '@/hooks/useDebounce';
import { ApiCiamClientResponse, ApiClientResponse } from '@/openapi/schema';

import CiamClientTile from '../CiamClientTile';
import ClientTile from '../ClientTile';
import { ClientSource, ViewType } from '../schemas';

function useFilteredCiamClients({
  ciamClients,
  portalClientIds,
  debouncedQuery,
}: {
  ciamClients: ApiCiamClientResponse[] | undefined;
  portalClientIds: Set<string>;
  debouncedQuery: string;
}) {
  const nonPortalCiamClients = useMemo(
    () => ciamClients?.filter((ciamClient) => !portalClientIds.has(ciamClient.id)) ?? [],
    [ciamClients, portalClientIds],
  );

  return useMemo(() => {
    if (nonPortalCiamClients.length === 0) return [];

    const lowerCaseQuery = debouncedQuery.toLowerCase();
    if (!lowerCaseQuery) {
      return nonPortalCiamClients;
    }

    return nonPortalCiamClients.filter((client) => {
      const clientName = client.name?.toLowerCase() || '';
      const clientId = client.id?.toLowerCase() || '';
      const liveMode = client.liveMode ? 'live' : 'test';
      const scopes = client.scopes ? client.scopes.map((scope) => scope.toLowerCase()) : [];

      return (
        clientName.includes(lowerCaseQuery) ||
        clientId.includes(lowerCaseQuery) ||
        liveMode.includes(lowerCaseQuery) ||
        scopes.some((scope) => scope.includes(lowerCaseQuery))
      );
    });
  }, [nonPortalCiamClients, debouncedQuery]);
}

function CiamClientsSection({
  ciamClients,
  ciamClientsLoading,
  ciamClientsError,
  portalClientIds,
  debouncedQuery,
  portalClientElements,
}: {
  ciamClients: ApiCiamClientResponse[] | undefined;
  ciamClientsLoading: boolean;
  ciamClientsError: unknown;
  portalClientIds: Set<string>;
  debouncedQuery: string;
  portalClientElements: JSX.Element[];
}) {
  const filteredCiamClients = useFilteredCiamClients({ ciamClients, portalClientIds, debouncedQuery });

  const ciamClientElements = filteredCiamClients.map((client) => (
    <Div bottom="small" key={client.id}>
      <CiamClientTile client={client} />
    </Div>
  ));

  const allClients = [...portalClientElements, ...ciamClientElements];

  return (
    <>
      {ciamClientsLoading && <ProgressIndicator size="small" type="linear" />}
      {ciamClientsError !== undefined && (
        <FormStatus bottom="small" state="error" stretch text="Failed to load CIAM clients." />
      )}
      {allClients.length > 0 ? allClients : <InfoCard stretch text="No clients match the current filters." />}
    </>
  );
}

export default function ClientView({
  consumerClients,
  isLoading,
  showView,
  query,
  clientSources,
  ciamClients,
  ciamClientsLoading,
  ciamClientsError,
}: {
  consumerClients: ApiClientResponse[] | undefined;
  isLoading: boolean;
  showView: (view: ViewType, query?: string) => void;
  query: string;
  clientSources: ClientSource[];
  ciamClients: ApiCiamClientResponse[] | undefined;
  ciamClientsLoading: boolean;
  ciamClientsError: unknown;
}) {
  const debouncedQuery = useDebounce(query, 300);

  const portalClientIds = useMemo(() => new Set(consumerClients?.map((c) => c.ciamClientId) ?? []), [consumerClients]);

  const filteredConsumerClients = useMemo(() => {
    if (!consumerClients) return [];

    const lowerCaseQuery = debouncedQuery.toLowerCase();
    if (!lowerCaseQuery) {
      return consumerClients.map((client) => (
        <Div bottom="small" key={client.ciamClientId}>
          <ClientTile client={client} showView={showView} />
        </Div>
      ));
    }

    return consumerClients
      .filter((client) => {
        const clientName = client.name?.toLowerCase() || '';
        const clientId = client.ciamClientId?.toLowerCase() || '';
        const clientDescription = client.description?.toLowerCase() || '';
        const appId = client.appId?.toLowerCase() || '';
        const liveMode = client.liveMode ? 'live' : 'test';
        const scopes = client.scopes ? client.scopes.map((scope) => scope.toLowerCase()) : [];
        const ciamApplicationId = client.ciamAppId?.toLowerCase() || '';

        return (
          clientName.includes(lowerCaseQuery) ||
          clientId.includes(lowerCaseQuery) ||
          clientDescription.includes(lowerCaseQuery) ||
          appId.includes(lowerCaseQuery) ||
          liveMode.includes(lowerCaseQuery) ||
          scopes.some((scope) => scope.includes(lowerCaseQuery)) ||
          ciamApplicationId.includes(lowerCaseQuery)
        );
      })
      .map((client) => (
        <Div bottom="small" key={client.ciamClientId}>
          <ClientTile client={client} showView={showView} />
        </Div>
      ));
  }, [consumerClients, debouncedQuery, showView]);

  const visiblePortalClients = clientSources.includes('portal') ? filteredConsumerClients : [];

  return (
    <>
      {isLoading && <ProgressIndicator size="small" type="linear" />}
      {clientSources.includes('ciam') ? (
        <CiamClientsSection
          ciamClients={ciamClients}
          ciamClientsError={ciamClientsError}
          ciamClientsLoading={ciamClientsLoading}
          debouncedQuery={debouncedQuery}
          portalClientElements={visiblePortalClients}
          portalClientIds={portalClientIds}
        />
      ) : !isLoading && clientSources.includes('portal') && consumerClients?.length === 0 ? (
        <InfoCard stretch>
          <P>
            No portal clients have been granted access to this integration service. Select CIAM above to see all
            clients.
          </P>
        </InfoCard>
      ) : visiblePortalClients.length > 0 ? (
        visiblePortalClients
      ) : (
        <InfoCard stretch text="No clients match the current filters." />
      )}
    </>
  );
}
